// Client-side first-page thumbnail generation for the upload tiles. This is
// PRESENTATION ASSISTANCE — it shows the customer a recognizable preview of a
// PDF manuscript instead of a generic file icon. It computes nothing that flows
// to pricing or the order (CLAUDE.md §"Pricing logic lives in the ERP"); it only
// paints a thumbnail. Sibling of detectArtwork.ts, which reads print attributes
// (size/color/sides/page count) off the same files — different concern, same
// lazy discipline.
//
//   PDF  — pdf.js (pdfjs-dist) rasterizes page 1 to a <canvas>, exported as a
//          PNG object URL.
//
// Word documents aren't an artwork format and the manuscript slot is PDF-only
// (see shared/util/upload-file-types → uploadFileTypeRejection). They ARE
// accepted on the "just give us all the files" catch-all, where a person
// converts them — but the browser can't rasterize a .docx, so there is no
// .docx thumbnail path; those tiles fall back to the file-type badge.
//
// pdf.js is imported DYNAMICALLY (only once a PDF is actually in hand) so it
// never lands in the main bundle — flat-sheet / USB customers who never upload
// a manuscript never download it. Same lazy-import discipline as detectArtwork.
//
// Returns null whenever we can't confidently produce a thumbnail (encrypted /
// malformed PDF). The caller renders a branded file-type badge in that case.

import { loadPdfjs } from './loadPdfjs.js';

export type DocThumbnailKind = 'pdf';

// Whether a file/asset is one we can attempt a document thumbnail for, decided
// by MIME type with a filename-extension fallback — OS file pickers report
// MIME types inconsistently.
export function docThumbnailKind(
  content_type: string,
  filename: string,
): DocThumbnailKind | null {
  const name = filename.toLowerCase();
  if (content_type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  // Anything else isn't a document we render — caller shows the branded badge.
  return null;
}

// Longest-side cap for the rendered PDF page, in device px. The tile preview
// box is ~160px CSS; render at ~2× for crispness on hi-dpi without ballooning
// canvas memory on a large-format (e.g. 11×17) page.
const PDF_THUMB_MAX_PX = 320;

// Skip thumbnailing past this source size — rasterizing a very large PDF
// client-side risks an OOM stall for little gain. Intentionally mirrors the
// backend's MAX_PREVIEW_PROXY_BYTES so a file over the cap shows the branded
// badge consistently whether it's being uploaded (local File) or revisited
// (proxied bytes that the server won't send).
const MAX_THUMBNAIL_SOURCE_BYTES = 25 * 1024 * 1024;

// Render the first page (PDF) from the given bytes and return an object URL the
// caller can drop into an <img>. The caller owns the URL and MUST revoke it
// (URL.revokeObjectURL) on unmount. Returns null when no thumbnail can be
// produced — never throws.
export async function buildDocThumbnailUrl(blob: Blob): Promise<string | null> {
  // Oversized source → branded badge (see MAX_THUMBNAIL_SOURCE_BYTES).
  if (blob.size > MAX_THUMBNAIL_SOURCE_BYTES) return null;
  try {
    return await renderPdfFirstPage(blob);
  } catch (err) {
    // Warn (not silent) so a pdf.js load/parse failure is diagnosable rather
    // than looking like "the thumbnail just didn't show".
    console.warn(
      '[doc-thumbnail] couldn’t build a thumbnail — falling back to a file-type badge.',
      err,
    );
    return null;
  }
}

async function renderPdfFirstPage(blob: Blob): Promise<string | null> {
  const pdfjs = await loadPdfjs();

  const data = new Uint8Array(await blob.arrayBuffer());
  const task = pdfjs.getDocument({ data });
  const doc = await task.promise;
  try {
    const page = await doc.getPage(1);
    const unscaled = page.getViewport({ scale: 1 });
    // Fit the page's longest side to PDF_THUMB_MAX_PX; never upscale past it.
    const scale = Math.min(
      PDF_THUMB_MAX_PX / Math.max(unscaled.width, unscaled.height),
      4,
    );
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // White backing so a page with a transparent background doesn't render on
    // the dark tile surface — it should read as a sheet of paper.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;

    return await new Promise<string | null>((resolve) => {
      canvas.toBlob(
        (out) => resolve(out ? URL.createObjectURL(out) : null),
        'image/png',
      );
    });
  } finally {
    // Free the worker + parsed document; we only ever needed page 1.
    await doc.destroy();
  }
}
